import { EChartsOption, SeriesOption } from 'echarts';
import {
    DateString,
    SimulationResultData,
} from 'routes/HumanResourcesModule/business/types';
import {
    DEFAULT_OPTIONS,
    currencyFormatter,
    tooltipFormatter,
} from 'routes/HumanResourcesModule/components/HrChart/eChartHelpers';
import { extractDates } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import {
    HISTORIC_AMOUNT,
    PAY_COMPONENT,
    SIMULATED_AMOUNT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import {
    getDateColumnLabel,
    getPivotedField,
} from 'routes/HumanResourcesModule/helpers/dataHelpers';
import _ from 'lodash';
import {
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
} from 'routes/HumanResourcesModule/business/definitions';
import { COLOR_SCALES } from 'styles/theme';
import {
    getPositionValue,
    PositionMappingValue,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { Theme } from '@mui/material';
import numbro from 'numbro';
import { keyIndicatorsCurrencyFormatter } from 'routes/HumanResourcesModule/helpers/formatHelpers';
import { generateColorMapping, getSerieColor } from './colorHelpers';

export type AmountField = typeof HISTORIC_AMOUNT | typeof SIMULATED_AMOUNT;

// Gets all the keys of a SimulationResultData object that are a date-pivoted amount key
function extractPivotedAmountMetrics(
    data: SimulationResultData[],
): AmountField[] {
    if (data.length === 0) return [];
    const metricKeys = Object.keys(data[0])
        .filter(
            (key) =>
                key.includes(`${HISTORIC_AMOUNT}..`) ||
                key.includes(`${SIMULATED_AMOUNT}..`),
        )
        .map((key) => key.split('..')[0] as AmountField);
    return Array.from(new Set(metricKeys));
}

// Gets the name of a serie in function of its grouped field and its metric
function getSerieName(
    group: string | number,
    groupByField: string,
    metric: string,
    t: TranslationT,
) {
    const metricName = t(`humanResources.simulation.${metric}`);
    return [ORG_CHART_HR_NAME, PAY_COMPONENT].includes(groupByField)
        ? `${group} - ${metricName}`
        : `${getPositionValue(group as PositionMappingValue)} - ${metricName}`;
}

/**
 * Get the result data grouped by the existing values of the given groupBy field
 * @param data The simulation data results to group by the wanted field
 * @param groupByField the SimulationResultData key field from which we want to group by the values
 * @returns A record of each value of the grouped field with the list of the result lines it refers.
 */
function getGroupedData(
    data: SimulationResultData[],
    groupByField: keyof SimulationResultData,
): Record<SimulationResultData[typeof groupByField], SimulationResultData[]> {
    const transformedData =
        groupByField === ORG_CHART_HR_NAME
            ? data.map((item) => ({
                  ...item,
                  [groupByField]:
                      item[ORG_CHART_HR_MAPPING] || item[groupByField],
              }))
            : data;

    return _.groupBy(transformedData, groupByField);
}

/**
 * Extract the metrics, groups the data and build echart series from it
 * @param data The list of SimulationResultData to show in the chart
 * @param dates The list of the extracted dates that will serve as X-axis values
 * @param groupByField The field defining the groups included in each bar of the bar charts
 *                     Each existing value of this field will be used to create echart serie
 * @param t The translator
 * @returns The list of series for each groupByField's value/metric combination
 */
function getEchartSeries(
    data: SimulationResultData[],
    dates: DateString[],
    groupByField: keyof SimulationResultData,
    t: TranslationT,
): SeriesOption[] {
    const metrics = extractPivotedAmountMetrics(data);

    // Get the result data grouped by the groupByField's existing values
    const groupedData = getGroupedData(data, groupByField);
    const serieNames = Object.keys(groupedData);
    const colorMapping = generateColorMapping(serieNames, COLOR_SCALES.manty);

    // Build EChart Serie for each group and each metric
    const series: SeriesOption[] = [];
    Object.keys(groupedData).forEach((group) => {
        metrics.forEach((metric) => {
            const seriesItem: SeriesOption = {
                name: getSerieName(group, groupByField, metric, t),
                type: 'bar',
                stack: metric,
                color: getSerieColor(group, metric, colorMapping),
                data: dates.map((date) => {
                    const key = getPivotedField(metric, date);
                    return _.sumBy(groupedData[group], key);
                }),
            };
            series.push(seriesItem);
        });
    });

    return series;
}
// Takes the result data and the groupByField to return EChartOption
// to build a bar chart with dates as X-axis and amounts as Y-axis
// each bar is divided in several groups defined by the groupByField values
export function getEchartOptions(
    data: SimulationResultData[],
    groupByField: keyof SimulationResultData,
    isMonthly: boolean,
    t: TranslationT,
): EChartsOption {
    const dates = extractDates<DateString>(data);
    const series = getEchartSeries(data, dates, groupByField, t);
    const formattedDates = dates.map((date) =>
        getDateColumnLabel(date, isMonthly),
    );
    return {
        xAxis: {
            type: 'category',
            data: formattedDates,
        },
        yAxis: {
            type: 'value',
            name: t('humanResources.simulation.amounts'),
            nameTextStyle: { fontSize: 14, align: 'right' },
            axisLabel: {
                formatter: currencyFormatter,
            },
        },
        series,
        tooltip: {
            ...DEFAULT_OPTIONS.tooltip,
            formatter: (params) =>
                tooltipFormatter(params, series, formattedDates),
        },

        legend: {
            ...DEFAULT_OPTIONS.legend,
            selectedMode: false,
        },
    };
}

function getGaugeEchartSeries(
    value: number,
    maxValue: number,
    theme: Theme,
): SeriesOption {
    const max = maxValue === 0 ? 100 : maxValue;
    // Build EChart Serie for each group and each metric
    const series: SeriesOption = {
        type: 'gauge',
        min: 0,
        max,
        splitNumber: 1,
        radius: '80%',
        progress: {
            show: true,
            itemStyle: {
                color:
                    value >= max
                        ? theme.palette.red
                        : theme.palette.primary.main,
            },
            roundCap: true,
        },
        axisLine: {
            lineStyle: {
                color: [[1, theme.palette.common.white]],
            },
            roundCap: true,
        },
        axisTick: { show: false },
        pointer: { show: false },
        axisLabel: {
            show: true,
            distance: -70,
            padding: 40,
            formatter(v) {
                return numbro(v).format({
                    average: true,
                });
            },
        },
        tooltip: {
            show: false,
        },
        splitLine: {
            show: false,
        },
        detail: {
            fontSize: '1rem',
            offsetCenter: [0, -10],

            formatter(dataValue) {
                return keyIndicatorsCurrencyFormatter(dataValue);
            },
        },
        data: [
            {
                value,
                name: `${
                    maxValue > 0 ? ((value * 100) / maxValue).toFixed(0) : 0
                }% réalisés`,
                detail: {
                    fontSize: '1rem',
                },
            },
        ],
    };

    return series;
}
export function getGaugeEchartOptions(
    value: number,
    maxValue: number,
    theme: Theme,
): EChartsOption {
    const series = getGaugeEchartSeries(value, maxValue, theme);
    return {
        textStyle: { fontFamily: 'Poppins' },
        series,
    };
}
